/**
 * This class is used for configuration of the firebase. For google maps api, the script is in index.html in public folder
 * This class also returns different firebase components like authentication, storage etc.
 *
 * The Firestore SDK here is `firebase/firestore/lite` (REST transport, no realtime
 * streaming channel) wrapped in a small compat-style shim so the rest of the codebase
 * keeps the familiar `db.collection(...).where(...).get()` chains and snapshot shape
 * (`.exists` as a property, `.data()`, `.docs`, `.forEach`). The lite SDK is a fraction
 * of the size of the full/compat Firestore build, which is what keeps it out of the
 * critical mobile bundle. Analytics and Functions are lazy-loaded facades so they do
 * not weigh down the initial chunk either.
 */
import { initializeApp } from 'firebase/app';
import {
    getFirestore,
    collection as liteCollection,
    collectionGroup as liteCollectionGroup,
    doc as liteDoc,
    query as liteQuery,
    where as liteWhere,
    orderBy as liteOrderBy,
    limit as liteLimit,
    startAfter as liteStartAfter,
    getDoc as liteGetDoc,
    getDocs as liteGetDocs,
    setDoc as liteSetDoc,
    updateDoc as liteUpdateDoc,
    deleteDoc as liteDeleteDoc,
    addDoc as liteAddDoc,
    writeBatch as liteWriteBatch,
    runTransaction as liteRunTransaction,
    documentId,
} from 'firebase/firestore/lite';

// Your web app's Firebase configuration
const firebaseConfig = {
    apiKey: 'AIzaSyCkPmW47ASmRYjifguX7mIRKX5gNmChYto',
    authDomain: 'optimx-sports.firebaseapp.com',
    projectId: 'optimx-sports',
    storageBucket: 'optimx-sports.appspot.com',
    messagingSenderId: '671798631941',
    appId: '1:671798631941:web:e865a9da16107dc020466c',
    measurementId: 'G-1MQ7GWY924',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

// Snapshots from lite expose `.exists()` as a method; the compat API (and all of our
// call sites) treat it as a property, so every snapshot is wrapped before it leaves
// this module. `liteSnap` keeps the raw snapshot for query cursors (startAfter).
const wrapDocSnapshot = (snap: any): any => {
    return {
        id: snap.id,
        exists: snap.exists(),
        data: () => { return snap.data(); },
        liteSnap: snap,
    };
};

const wrapQuerySnapshot = (snap: any): any => {
    const docs = snap.docs.map(wrapDocSnapshot);
    return {
        docs,
        empty: snap.empty,
        size: snap.size,
        forEach: (callback: Function) => { docs.forEach((docSnap: any) => { callback(docSnap); }); },
        liteSnap: snap,
    };
};

// The lite SDK sends each read as a single fetch and, unlike the full SDK, never retries.
// A fetch the browser drops (flaky signal, or iOS Safari backgrounding the tab) surfaces as
// `unknown` ("Request failed with error: undefined"); 5xx responses map to the other codes.
const TRANSIENT_ERROR_CODES = ['unknown', 'unavailable', 'deadline-exceeded', 'internal'];
const READ_RETRY_DELAYS_MS = [400, 1200];
const MAX_WAIT_FOR_VISIBLE_MS = 30000;

export const isTransientFirestoreError = (error: any): boolean => {
    return TRANSIENT_ERROR_CODES.includes(error?.code);
};

// Tracks whether the page is unloading, so errors caused by the browser cutting requests off
// as the visitor leaves can be told apart from real failures. Only the current state counts:
// retries wait for the tab to be visible again, so a failure after the visitor is back is real.
let pageUnloading = false;
if (typeof document !== 'undefined') {
    window.addEventListener('pagehide', () => { pageUnloading = true; });
    window.addEventListener('pageshow', () => { pageUnloading = false; });
}

export const wasPageInterrupted = (): boolean => {
    return pageUnloading || (typeof document !== 'undefined' && document.visibilityState === 'hidden');
};

const wait = (ms: number) => { return new Promise((resolve) => { setTimeout(resolve, ms); }); };

// Retrying while the tab is in the background just fails again, so hold the retry until the
// visitor comes back (bounded, so a tab left in the background does not hang forever).
const waitUntilVisible = () => {
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
        const done = () => {
            document.removeEventListener('visibilitychange', onChange);
            clearTimeout(timer);
            resolve();
        };
        const onChange = () => { if (document.visibilityState !== 'hidden') { done(); } };
        const timer = setTimeout(done, MAX_WAIT_FOR_VISIBLE_MS);
        document.addEventListener('visibilitychange', onChange);
    });
};

const withReadRetry = async (read: Function): Promise<any> => {
    for (let attempt = 0; ; attempt += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            return await read();
        } catch (error) {
            if (pageUnloading || attempt >= READ_RETRY_DELAYS_MS.length || !isTransientFirestoreError(error)) {
                throw error;
            }
            // eslint-disable-next-line no-await-in-loop
            await wait(READ_RETRY_DELAYS_MS[attempt]);
            // eslint-disable-next-line no-await-in-loop
            await waitUntilVisible();
        }
    }
};

const unwrapCursor = (value: any): any => {
    return (value && value.liteSnap) ? value.liteSnap : value;
};

const makeQuery = (base: any, constraints: any[]): any => {
    return {
        where: (field: any, op: any, value: any) => { return makeQuery(base, [...constraints, liteWhere(field, op, value)]); },
        orderBy: (field: any, direction?: any) => { return makeQuery(base, [...constraints, liteOrderBy(field, direction)]); },
        limit: (count: number) => { return makeQuery(base, [...constraints, liteLimit(count)]); },
        startAfter: (...args: any[]) => { return makeQuery(base, [...constraints, liteStartAfter(...args.map(unwrapCursor))]); },
        get: () => { return withReadRetry(() => { return liteGetDocs(liteQuery(base, ...constraints)); }).then(wrapQuerySnapshot); },
    };
};

const makeDocReference = (ref: any): any => {
    return {
        id: ref.id,
        liteRef: ref,
        collection: (name: string) => { return makeCollectionReference(liteCollection(ref, name)); },
        get: () => { return withReadRetry(() => { return liteGetDoc(ref); }).then(wrapDocSnapshot); },
        set: (data: any) => { return liteSetDoc(ref, data); },
        update: (data: any) => { return liteUpdateDoc(ref, data); },
        delete: () => { return liteDeleteDoc(ref); },
    };
};

const makeCollectionReference = (base: any): any => {
    return {
        ...makeQuery(base, []),
        doc: (id: string) => { return makeDocReference(liteDoc(base, id)); },
        add: (data: any) => { return liteAddDoc(base, data); },
    };
};

// For `where(documentId(), 'in', ids)` batch reads (one request instead of one per document).
export { documentId };

export const db = {
    collection: (name: string) => { return makeCollectionReference(liteCollection(firestore, name)); },
    collectionGroup: (name: string) => { return makeQuery(liteCollectionGroup(firestore, name), []); },
    batch: () => {
        const batch = liteWriteBatch(firestore);
        return {
            set: (ref: any, data: any) => { batch.set(ref.liteRef, data); },
            update: (ref: any, data: any) => { batch.update(ref.liteRef, data); },
            delete: (ref: any) => { batch.delete(ref.liteRef); },
            commit: () => { return batch.commit(); },
        };
    },
    runTransaction: (updateFunction: Function) => {
        return liteRunTransaction(firestore, (transaction: any) => {
            return updateFunction({
                get: (ref: any) => { return transaction.get(ref.liteRef).then(wrapDocSnapshot); },
                set: (ref: any, data: any) => { return transaction.set(ref.liteRef, data); },
                update: (ref: any, data: any) => { return transaction.update(ref.liteRef, data); },
                delete: (ref: any) => { return transaction.delete(ref.liteRef); },
            });
        });
    },
};

// Analytics facade with the compat call shape (`analytics.logEvent(...)`), backed by a
// lazily imported modular SDK so analytics never blocks or bloats the initial bundle.
let analyticsPromise: Promise<any> | null = null;
const withAnalytics = (callback: Function) => {
    if (analyticsPromise === null) {
        analyticsPromise = import('firebase/analytics')
            .then((module) => { return { module, instance: module.getAnalytics(app) }; })
            .catch(() => { return null; });
    }
    analyticsPromise.then((loaded: any) => {
        if (loaded !== null) {
            callback(loaded.module, loaded.instance);
        }
    });
};

export const analytics = {
    logEvent: (eventName: string, eventParams?: any) => {
        withAnalytics((module: any, instance: any) => { module.logEvent(instance, eventName, eventParams); });
    },
    setUserProperties: (properties: any) => {
        withAnalytics((module: any, instance: any) => { module.setUserProperties(instance, properties); });
    },
};

// Functions facade with the compat call shape (`functions.httpsCallable(name)(data)`),
// lazily imported because checkout is the only flow that needs it.
export const functions = {
    httpsCallable: (name: string) => {
        return (data: any) => {
            return import('firebase/functions').then((module) => {
                return module.httpsCallable(module.getFunctions(app), name)(data);
            });
        };
    },
};
